import { useCallback } from 'react';
import type { InvoiceResource } from '../../../Entities/Invoice';
import { DeepPartial } from '../../../utils';
import { HttpError, useCreate, useNotify, useRefresh, useUpdateMany } from 'react-admin';

export default function useCreateInvoice(
  selectedIds: string[],
  { deselectIds, prefetch }: { deselectIds: () => void; prefetch: string[] },
): [(invoice: DeepPartial<InvoiceResource>) => void, { isPending: boolean; isLoading: boolean }] {
  const [create, { isPending: createPending, isLoading: createLoading }] = useCreate();
  const [updateMany, { isPending: updatePending, isLoading: updateLoading }] = useUpdateMany();

  const notify = useNotify();
  const refresh = useRefresh();

  const isPending = createPending || updatePending;
  const isLoading = createLoading || updateLoading;

  const callback = useCallback(
    (invoice: DeepPartial<InvoiceResource>) => {
      if (isPending || isLoading) {
        return;
      }

      const onUpdateSuccess = () => {
        notify('Invoice created successfully!', { type: 'success' });
        refresh();
        deselectIds();
      };

      const onUpdateError = () => {
        const msg = 'Invoice created, but error marking items as sent to NetSuite';
        notify(msg, { type: 'error' });
      };

      const onCreateSuccess = () => {
        updateMany(
          'billing_items',
          {
            ids: selectedIds,
            data: {
              relationships: {
                billing_item_status: { data: { type: 'billing_item_statuses', id: '7' } },
              },
            },
            meta: { prefetch },
          },
          {
            onSuccess: onUpdateSuccess,
            onError: onUpdateError,
          },
        );
      };

      const onCreateError = (e: unknown) => {
        if (e instanceof HttpError) {
          notify(e.message, { type: 'error' });
        } else {
          notify('Error creating invoice', { type: 'error' });
          console.error('Error creating invoice', e);
        }
      };

      create('invoices', { data: invoice }, { onSuccess: onCreateSuccess, onError: onCreateError });
    },
    [create, deselectIds, isLoading, isPending, notify, prefetch, refresh, selectedIds, updateMany],
  );

  return [callback, { isPending, isLoading }];
}
